import React, { useRef } from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ReactMarkdown from 'react-markdown';

interface AngebotMarkupFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  minRows?: number;
}

const LIST_LINE = /^\s*(?:[-*•]|☐|□|\d+[.)])\s+/;

function wrapSelection(
  value: string,
  start: number,
  end: number,
  marker: string,
): { next: string; selStart: number; selEnd: number } {
  const selected = value.slice(start, end);
  const inner = selected || 'Text';
  const next = value.slice(0, start) + marker + inner + marker + value.slice(end);
  const selStart = start + marker.length;
  return { next, selStart, selEnd: selStart + inner.length };
}

function prefixBullets(value: string, start: number, end: number): { next: string; selStart: number; selEnd: number } {
  const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  const lineEndIdx = value.indexOf('\n', end);
  const lineEnd = lineEndIdx === -1 ? value.length : lineEndIdx;
  const block = value.slice(lineStart, lineEnd);
  const transformed = block
    .split('\n')
    .map((line) => {
      if (!line.trim() || LIST_LINE.test(line)) return line;
      return `- ${line}`;
    })
    .join('\n');
  return {
    next: value.slice(0, lineStart) + transformed + value.slice(lineEnd),
    selStart: lineStart,
    selEnd: lineStart + transformed.length,
  };
}

const AngebotMarkupField: React.FC<AngebotMarkupFieldProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  minRows = 4,
}) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const apply = (next: string, selStart: number, selEnd: number) => {
    onChange(next);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(selStart, selEnd);
    });
  };

  const onBold = () => {
    const el = inputRef.current;
    if (!el) return;
    const { next, selStart, selEnd } = wrapSelection(value, el.selectionStart ?? value.length, el.selectionEnd ?? value.length, '**');
    apply(next, selStart, selEnd);
  };

  const onItalic = () => {
    const el = inputRef.current;
    if (!el) return;
    const { next, selStart, selEnd } = wrapSelection(value, el.selectionStart ?? value.length, el.selectionEnd ?? value.length, '*');
    apply(next, selStart, selEnd);
  };

  const onBullet = () => {
    const el = inputRef.current;
    if (!el) return;
    const { next, selStart, selEnd } = prefixBullets(value, el.selectionStart ?? value.length, el.selectionEnd ?? value.length);
    apply(next, selStart, selEnd);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {!disabled && (
          <Box>
            <Tooltip title="Fett (**Text**)">
              <IconButton size="small" aria-label="Fett" onClick={onBold}>
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Kursiv (*Text*)">
              <IconButton size="small" aria-label="Kursiv" onClick={onItalic}>
                <FormatItalicIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Aufzählung">
              <IconButton size="small" aria-label="Aufzählung" onClick={onBullet}>
                <FormatListBulletedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      {disabled ? (
        <Box sx={{ px: 1.5, py: 1, border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 72 }}>
          <MarkupPreview value={value} />
        </Box>
      ) : (
        <TextField
          fullWidth
          multiline
          minRows={minRows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputRef={inputRef}
          placeholder={'**fett**, *kursiv*, Zeile mit - für Aufzählung, 1. für Nummerierung'}
          helperText="Nur diese Auszeichnungen. Überschriften, Tabellen, Links und Bilder bleiben reiner Text."
        />
      )}
      {!disabled && value.trim() && (
        <Box sx={{ mt: 1, px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">Vorschau</Typography>
          <MarkupPreview value={value} />
        </Box>
      )}
    </Box>
  );
};

const MarkupPreview: React.FC<{ value: string }> = ({ value }) => {
  if (!value.trim()) {
    return <Typography variant="body2" color="text.secondary">–</Typography>;
  }
  return (
    <Box sx={{ '& p': { my: 0.5 }, '& ul, & ol': { my: 0.5, pl: 3 }, '& li': { mb: 0.25 } }}>
      <ReactMarkdown allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li']} unwrapDisallowed>
        {value}
      </ReactMarkdown>
    </Box>
  );
};

export default AngebotMarkupField;
